import { getReviewerConfig, isReviewer } from '@/config/reviewers';
import { Amplify } from 'aws-amplify';
import * as SecureStore from './secureStore';

const CURRENT_USERNAME_KEY = 'current_username';

export interface DynamicEnv {
  apiHost: string;
  cognitoClientId: string;
  cognitoUserPoolId: string;
  associatedDomain: string;
  currentUsername: string | null;
  isReviewMode: boolean;
}

// Default environment variables (fallback)
const defaultEnv = {
  apiHost: process.env['EXPO_PUBLIC_API_HOST']!,
  cognitoClientId: process.env['EXPO_PUBLIC_COGNITO_CLIENT_ID']!,
  cognitoUserPoolId: process.env['EXPO_PUBLIC_COGNITO_USER_POOL_ID']!,
  associatedDomain: process.env['EXPO_PUBLIC_ASSOCIATED_DOMAIN']!,
};

let currentUsername: string | null = null;
let dynamicEnv: DynamicEnv | null = null;

export async function getCurrentUsername(): Promise<string | null> {
  // Always read from SecureStore to ensure we have the latest value
  // This is important on native where the value might have been set asynchronously
  try {
    const storedUsername = await SecureStore.getItemAsync(CURRENT_USERNAME_KEY);
    // Update in-memory cache
    currentUsername = storedUsername;
    return storedUsername;
  } catch (error) {
    console.error('Error getting current username:', error);
    // Fall back to in-memory value if SecureStore read fails
    return currentUsername;
  }
}

export async function setCurrentUsername(username: string | null): Promise<void> {
  try {
    if (username) {
      await SecureStore.setItemAsync(CURRENT_USERNAME_KEY, username);
      // Update in-memory cache only after successful write
      currentUsername = username;
      console.log('Username stored in SecureStore:', username);
    } else {
      await SecureStore.deleteItemAsync(CURRENT_USERNAME_KEY);
      // Update in-memory cache only after successful delete
      currentUsername = null;
      console.log('Username removed from SecureStore');
    }
  } catch (error) {
    console.error('Error setting current username:', error);
    // Still update in-memory cache even if SecureStore fails
    currentUsername = username;
  }
}

export async function getDynamicEnv(): Promise<DynamicEnv> {
  // Always refresh username from storage to ensure we have the latest value
  // This is important on native where the username might be loaded asynchronously
  const storedUsername = await getCurrentUsername();
  
  // Only use cached dynamicEnv if it exists AND the username hasn't changed
  if (dynamicEnv && dynamicEnv.currentUsername === storedUsername) {
    console.log('[getDynamicEnv] Using cached env for username:', storedUsername, 'apiHost:', dynamicEnv.apiHost);
    return dynamicEnv;
  }
  
  // Username changed or cache doesn't exist - rebuild dynamicEnv
  const username = storedUsername;
  const reviewerConfig = username ? getReviewerConfig(username) : null;
  
  dynamicEnv = {
    apiHost: reviewerConfig?.apiHost || defaultEnv.apiHost,
    cognitoClientId: reviewerConfig?.cognitoClientId || defaultEnv.cognitoClientId,
    cognitoUserPoolId: reviewerConfig?.cognitoUserPoolId || defaultEnv.cognitoUserPoolId,
    associatedDomain: reviewerConfig?.associatedDomain || defaultEnv.associatedDomain,
    currentUsername: username,
    isReviewMode: !!username && isReviewer(username),
  };
  
  console.log('[getDynamicEnv] Built new env for username:', username, 'apiHost:', dynamicEnv.apiHost, 'isReviewMode:', dynamicEnv.isReviewMode);
  
  return dynamicEnv;
}

export async function switchConfiguration(username: string | null): Promise<void> {
  console.log('[switchConfiguration] Starting configuration switch for username:', username);
  
  // Update stored username - ensure it's fully written to SecureStore
  await setCurrentUsername(username);
  
  // Verify the username was stored correctly (important on native)
  const verifyUsername = await getCurrentUsername();
  if (verifyUsername !== username) {
    console.warn('[switchConfiguration] Username mismatch after storage. Expected:', username, 'Got:', verifyUsername);
    // Retry once
    await setCurrentUsername(username);
    const verifyUsername2 = await getCurrentUsername();
    if (verifyUsername2 !== username) {
      console.error('[switchConfiguration] Username still mismatched after retry. Expected:', username, 'Got:', verifyUsername2);
    }
  }
  
  // Clear cached environment to force refresh
  dynamicEnv = null;
  console.log('[switchConfiguration] Cleared dynamicEnv cache');
  
  // Clear any cached tokens and reconfigure Amplify
  if (username && isReviewer(username)) {
    console.log('[switchConfiguration] User is a reviewer, reconfiguring Amplify');
    await reconfigureAmplify();
  } else {
    console.log('[switchConfiguration] User is not a reviewer, resetting to default configuration');
    // Reset to default configuration
    await reconfigureAmplify();
  }
}

export async function reconfigureAmplify(): Promise<void> {
  try {
    const env = await getDynamicEnv();
    
    // Clear any existing Amplify configuration and cached tokens
    Amplify.configure({});
    
    // Clear any stored tokens from secure store
    try {
      await SecureStore.deleteItemAsync('amplify-authenticator-authToken');
      await SecureStore.deleteItemAsync('amplify-authenticator-refreshToken');
      await SecureStore.deleteItemAsync('amplify-authenticator-accessToken');
    } catch (error) {
      console.log('No cached tokens to clear or error clearing tokens:', error);
    }
    
    // Configure with new settings
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: env.cognitoUserPoolId,
          userPoolClientId: env.cognitoClientId,
        }
      }
    });
    
    console.log('Amplify reconfigured for user:', env.currentUsername);
  } catch (error) {
    console.error('Error reconfiguring Amplify:', error);
  }
}

export async function initializeConfiguration(): Promise<void> {
  // Load stored username on app start
  await getCurrentUsername();
  
  // If we have a stored username, reconfigure Amplify
  if (currentUsername && isReviewer(currentUsername)) {
    await reconfigureAmplify();
  }
}
